import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Checkbox } from './checkbox';
import { Button } from './button';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Multi-select filter dropdown using Popover + Checkboxes.
 *
 * @param {string}   label         – Placeholder text when nothing selected (e.g. "All Asset Types")
 * @param {Array}    options       – [{value, label}]
 * @param {string[]} selected      – Currently selected values
 * @param {Function} onSelectionChange – Called with updated string[] on every toggle
 * @param {string}   className     – Extra classes for the trigger button
 * @param {string}   testId        – data-testid for the trigger
 */
export function MultiSelectFilter({
  label,
  options = [],
  selected = [],
  onSelectionChange,
  className,
  testId,
}) {
  const [open, setOpen] = useState(false);

  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onSelectionChange(next);
  };

  const displayText = selected.length === 0
    ? label
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || selected[0]
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            'justify-between font-normal',
            selected.length > 0 && 'text-foreground',
            className,
          )}
        >
          <span className="truncate">{displayText}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 max-h-64 overflow-y-auto" align="start">
        {options.map(opt => (
          <label
            key={opt.value}
            className="flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-accent text-sm"
          >
            <Checkbox
              checked={selected.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
            />
            <span className="truncate">{opt.label}</span>
          </label>
        ))}
        {options.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">No options</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
